/**
 * Throwable equipment — one lethal and one tactical, thrown, bounced and triggered.
 *
 * The roster and its design notes live in `equipment.data.js`. This file is the
 * machine that flies them: one projectile integrator, one trigger state machine, and
 * a `_detonate` switch that hands off to whatever the piece actually does.
 *
 * ## Why one integrator for everything
 *
 * Because the difference between a frag and a smoke is *when* the flight ends and
 * *what* happens then — never how it flies. Building a projectile per equipment type
 * would duplicate gravity, bounce and the surface probe eleven times, and eleven
 * copies of a bounce is eleven places for a grenade to fall through the floor. So
 * the flight is written once and the `TRIGGER` enum decides what stops it.
 *
 * ## Cooking
 *
 * A timed lethal starts its fuse when the button goes *down*, not when the
 * projectile leaves the hand. That is the entire risk/reward of a cookable grenade —
 * hold it to deny the enemy time to run, hold it too long and it goes off in your
 * hand — and it is why the fuse lives on the pending throw rather than on the
 * projectile.
 *
 * ## What it does not do
 *
 * No resupply, no per-match count beyond the starting one of each. That is a
 * progression question rather than a physics one, and inventing an economy here
 * would put a balance decision inside a file about ballistics.
 */

import * as THREE from 'three';
import { EFFECT, EQUIPMENT, TRIGGER, DEFAULT_LOADOUT } from './equipment.data.js';
import { explode } from './streaks/common.js';
import { buildEquipmentModel } from '../weapons/models/equipment.js';
import { MEANS } from './MatchAuthority.js';

export const TUNING = {
  /** How many of each you carry into a life. */
  startCount: { lethal: 2, tactical: 2 },
  gravity: -19.5,
  /** Speed lost to friction each bounce, on top of the piece's own restitution. */
  friction: 0.72,
  /** Below this, a bouncing projectile is treated as at rest. */
  restSpeed: 0.9,
  /** Radius used for the sweep. Grenades are small; this keeps them out of walls. */
  radius: 0.11,
  /** Seconds from the button to the projectile leaving the hand. */
  windup: 0.18,
  /** Upward component added to the throw, so a flat look still lobs. */
  lift: 0.22,
  /** A thrown piece that never triggers is removed after this, as a safety net. */
  maxLife: 30,
  /** Ticks per second for burn pools and proximity checks. */
  tickHz: 10,
  /** What your own fire costs you. Less than a full share, on purpose. */
  selfBurnScale: 0.7,
};

const _p = new THREE.Vector3();
const _v = new THREE.Vector3();
const _d = new THREE.Vector3();
const _eye = new THREE.Vector3();
const _fwd = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);

export class Equipment {
  constructor(ctx, player) {
    this.ctx = ctx;
    this.player = player;

    /** What is equipped. Written by the loadout screen through `loadout:equipment`. */
    this.loadout = { ...DEFAULT_LOADOUT };
    this.count = { ...TUNING.startCount };

    /** Live projectiles and placed devices. */
    this.items = [];
    /** Burning pools, which outlive the projectile that made them. */
    this.pools = [];
    /** Devices waiting for their owner's second press. */
    this.remotes = [];

    /** Button held, fuse burning, not yet thrown. */
    this.pending = null;

    this.thrown = 0;
    this.detonations = 0;
    this._tick = 0;

    this._offs = [
      ctx.events.on('loadout:equipment', (p) => this.setLoadout(p)),
      ctx.events.on('game:state', (p) => {
        if (p?.state === 'playing') this._refill();
      }),
    ];

    ctx.engine.registerUpdate((dt) => this.update(dt), 'sim');
  }

  /* ------------------------------- loadout -------------------------------- */

  setLoadout(p) {
    if (!p) return;
    if (p.lethal && EQUIPMENT[p.lethal]) this.loadout.lethal = p.lethal;
    if (p.tactical && EQUIPMENT[p.tactical]) this.loadout.tactical = p.tactical;
    this._publish();
  }

  _refill() {
    this.count.lethal = TUNING.startCount.lethal;
    this.count.tactical = TUNING.startCount.tactical;
    this._publish();
  }

  _publish() {
    this.ctx.events.emit('equipment:state', {
      lethal: this.loadout.lethal,
      tactical: this.loadout.tactical,
      lethalCount: this.count.lethal,
      tacticalCount: this.count.tactical,
      cooking: this.pending ? this.pending.slot : null,
      cookLeft: this.pending ? Math.max(0, this.pending.fuseLeft) : 0,
    });
  }

  /* --------------------------------- input -------------------------------- */

  update(dt) {
    const input = this.ctx.input;
    if (!input || this.player?.dead) {
      // Dropping the pending throw on death rather than letting it cook is the
      // kinder reading: dying should not also detonate a grenade at your feet.
      this.pending = null;
    } else {
      this._handleInput(dt, input);
    }

    this._step(dt);
    this._stepPools(dt);
    this._tick += dt;
    if (this._tick >= 1 / TUNING.tickHz) {
      this._proximityScan();
      this._tick = 0;
    }
  }

  _handleInput(dt, input) {
    // A remote charge already on a wall consumes the button: the second press is the
    // detonation, not another throw. Checked first, or you would throw your last
    // charge trying to set off the one already stuck to a doorway.
    if (this.remotes.length && input.actionPressed('grenade')) {
      this._detonateRemotes();
      return;
    }

    if (!this.pending) {
      if (input.actionPressed('grenade')) this._begin('lethal');
      else if (input.actionPressed('tactical')) this._begin('tactical');
      return;
    }

    const pend = this.pending;
    const held = input.action(pend.slot === 'lethal' ? 'grenade' : 'tactical');
    pend.windup -= dt;

    if (pend.cookable) {
      pend.fuseLeft -= dt;
      if (pend.fuseLeft <= 0) {
        // Cooked too long. It goes off where it is, which is the only honest
        // outcome and the reason cooking is a decision at all.
        const at = _p.copy(this.player.position);
        at.y += 1.2;
        this._detonate(EQUIPMENT[pend.id], at.clone(), null);
        this.pending = null;
        this._publish();
        return;
      }
      this._publish();
    }

    // Released, or the wind-up finished for a piece that cannot be cooked.
    if (!held || (!pend.cookable && pend.windup <= 0)) this._release();
  }

  _begin(slot) {
    const id = this.loadout[slot];
    const def = EQUIPMENT[id];
    if (!def || this.count[slot] <= 0) {
      this.ctx.events.emit('equipment:empty', { slot });
      return;
    }
    this.pending = {
      slot,
      id,
      cookable: !!def.cookable,
      fuseLeft: def.fuse ?? 0,
      windup: TUNING.windup,
    };
    this.ctx.events.emit('equipment:cook', { slot, id, fuse: def.fuse ?? 0 });
    this._publish();
  }

  _release() {
    const pend = this.pending;
    this.pending = null;
    if (!pend) return;
    const def = EQUIPMENT[pend.id];
    if (!def) return;

    this.count[pend.slot] = Math.max(0, this.count[pend.slot] - 1);

    // A stim is not thrown at all — it is used, on the spot. Modelling it as a
    // projectile with zero speed would make it a grenade you drop on your own feet.
    if (def.effect === EFFECT.STIM) {
      this._applyStim(def);
      this.thrown++;
      this._publish();
      return;
    }

    const cam = this.ctx.camera;
    cam.getWorldDirection(_fwd);
    _eye.setFromMatrixPosition(cam.matrixWorld);

    const speed = def.throwSpeed ?? 16;
    _v.copy(_fwd).multiplyScalar(speed);
    _v.y += speed * TUNING.lift;
    // Inherit the thrower's own motion, or a grenade thrown while sprinting lands
    // behind where the player expected it to.
    const pv = this.player?.velocity;
    if (pv) _v.add(_d.copy(pv).multiplyScalar(0.6));

    this.items.push({
      def,
      slot: pend.slot,
      pos: _eye.clone().addScaledVector(_fwd, 0.45),
      vel: _v.clone(),
      spin: new THREE.Vector3((Math.random() * 2 - 1) * 9, 0, (Math.random() * 2 - 1) * 9),
      fuseLeft: pend.cookable ? pend.fuseLeft : (def.fuse ?? 0),
      stuck: false,
      armed: false,
      armT: 0,
      age: 0,
      mesh: this._makeMesh(def),
    });

    this.thrown++;
    this.ctx.events.emit('equipment:throw', { slot: pend.slot, id: def.id });
    this.ctx.events.emit('audio:play', { id: 'gear_drop', volume: 0.4, pitch: 1.3 });
    this._publish();
  }

  /* ------------------------------- projectile ------------------------------ */

  /**
   * A visible body for the projectile.
   *
   * Deliberately crude: a thrown object is in flight for under two seconds, is
   * tumbling, and is never the thing the player is looking at. Detail here would
   * cost draw calls on the tier that just spent a release getting them back.
   */
  _makeMesh(def) {
    const lethal = def.effect === EFFECT.BLAST || def.effect === EFFECT.BURN;
    const group = new THREE.Group();

    // The real model, at true scale, scaled up a little. A 9 cm grenade is correct
    // and unreadable: thrown across a sunlit yard on a phone it is a few pixels of
    // nothing, which is how a player concludes the button did not work. 1.5x keeps
    // the silhouette honest while making it something the eye can actually follow.
    const model = buildEquipmentModel(def.id);
    if (model) {
      model.scale.setScalar(1.5);
      group.add(model);
    } else {
      // No builder for this id. An unrecognisable projectile still beats no
      // projectile, so fall back rather than throwing.
      group.add(
        new THREE.Mesh(
          new THREE.IcosahedronGeometry(0.11, 0),
          new THREE.MeshStandardMaterial({
            color: lethal ? 0x39423a : 0x33404d,
            roughness: 0.6,
            metalness: 0.3,
          })
        )
      );
    }

    // A live indicator light, additive so the bloom pass picks it up. This is the
    // part you actually track with your eye — the body reads as a silhouette, the
    // light reads as "that is mine and it is armed".
    const dot = new THREE.Mesh(
      new THREE.SphereGeometry(0.055, 8, 6),
      new THREE.MeshBasicMaterial({
        color: lethal ? 0xff5a2a : 0x3fd0ff,
        transparent: true,
        opacity: 0.95,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      })
    );
    group.add(dot);
    group.userData.dot = dot;

    this.ctx.scene.add(group);
    return group;
  }

  _step(dt) {
    const physics = this.ctx.physics;

    for (let i = this.items.length - 1; i >= 0; i--) {
      const it = this.items[i];
      it.age += dt;
      const def = it.def;

      if (!it.stuck) {
        it.vel.y += TUNING.gravity * dt;
        _d.copy(it.vel).multiplyScalar(dt);
        const dist = _d.length();

        if (dist > 1e-5 && physics?.raycast) {
          _d.multiplyScalar(1 / dist);
          const hit = physics.raycast(it.pos, _d, dist + TUNING.radius);
          if (hit?.hit) {
            const stops =
              def.trigger === TRIGGER.IMPACT ||
              def.trigger === TRIGGER.STICKY ||
              def.trigger === TRIGGER.REMOTE ||
              def.trigger === TRIGGER.PROXIMITY;
            // Land just off the surface, not on it: a projectile resting exactly in
            // the plane flickers against it and its blast origin can end up on the
            // wrong side of a thin wall.
            it.pos.copy(hit.point).addScaledVector(hit.normal, TUNING.radius);
            if (stops) {
              it.stuck = true;
              it.vel.set(0, 0, 0);
              this._onLanded(it, hit);
              if (def.trigger === TRIGGER.IMPACT) {
                this._detonate(def, it.pos.clone(), hit);
                this._remove(i);
                continue;
              }
            } else {
              this._bounce(it, hit, def);
            }
          } else {
            it.pos.addScaledVector(it.vel, dt);
          }
        } else {
          it.pos.addScaledVector(it.vel, dt);
        }

        if (!it.stuck && it.vel.lengthSq() < TUNING.restSpeed ** 2 && it.pos.y < 60) {
          // Rolling to a stop is not "landed" for a timed grenade — it keeps
          // counting — but it stops the mesh jittering.
          it.vel.multiplyScalar(0.5);
        }
      }

      // rotation is cosmetic; a stuck device holds still
      if (!it.stuck) {
        it.mesh.rotation.x += it.spin.x * dt;
        it.mesh.rotation.z += it.spin.z * dt;
      }
      it.mesh.position.copy(it.pos);

      // A device that is sitting on the ground waiting has to announce itself, or a
      // player who placed a mine has no way to tell it from a dud. Slow pulse while
      // arming, fast once it is live.
      const dot = it.mesh.userData?.dot;
      if (dot) {
        const waiting = it.stuck && (def.trigger === TRIGGER.PROXIMITY || def.trigger === TRIGGER.REMOTE);
        const hz = waiting ? (it.armed ? 3.2 : 1.2) : 0;
        dot.material.opacity = hz
          ? 0.35 + 0.6 * (0.5 + 0.5 * Math.sin(it.age * hz * Math.PI * 2))
          : 0.95;
      }

      if (def.trigger === TRIGGER.PROXIMITY && it.stuck) {
        it.armT += dt;
        if (!it.armed && it.armT >= (def.armTime ?? 1)) {
          it.armed = true;
          this.ctx.events.emit('audio:play', {
            id: 'ui_open',
            position: it.pos.clone(),
            volume: 0.3,
          });
        }
      }

      const timed = def.trigger === TRIGGER.TIMED || def.trigger === TRIGGER.STICKY;
      if (timed) {
        it.fuseLeft -= dt;
        if (it.fuseLeft <= 0) {
          this._detonate(def, it.pos.clone(), null);
          this._remove(i);
          continue;
        }
      }

      if (it.age > TUNING.maxLife) this._remove(i);
    }
  }

  _bounce(it, hit, def) {
    const restitution = def.bounce ?? 0.3;
    const n = hit.normal;
    const dot = it.vel.dot(n);
    it.vel.addScaledVector(n, -(1 + restitution) * dot);
    it.vel.multiplyScalar(TUNING.friction);
    this.ctx.events.emit('audio:play', {
      id: 'gear_drop',
      position: it.pos.clone(),
      volume: 0.28,
      pitch: 1.5,
    });
  }

  _onLanded(it, hit) {
    if (it.def.trigger === TRIGGER.REMOTE) this.remotes.push(it);
    void hit;
  }

  _remove(i) {
    const it = this.items[i];
    if (!it) return;
    this.ctx.scene.remove(it.mesh);
    // A group now, not a single mesh — traverse rather than reaching for `.geometry`,
    // which is undefined on a Group and would throw on every despawn.
    // Geometry is per-instance and safe to free. Materials are shared module-wide by
    // the equipment model layer, so disposing them here would blank every grenade
    // thrown after the first one despawned.
    it.mesh.traverse((o) => {
      if (o.isMesh) o.geometry?.dispose?.();
    });
    const r = this.remotes.indexOf(it);
    if (r >= 0) this.remotes.splice(r, 1);
    this.items.splice(i, 1);
  }

  _detonateRemotes() {
    for (let i = this.items.length - 1; i >= 0; i--) {
      const it = this.items[i];
      if (it.def.trigger !== TRIGGER.REMOTE) continue;
      this._detonate(it.def, it.pos.clone(), null);
      this._remove(i);
    }
    this.remotes.length = 0;
  }

  /**
   * Anything armed and tripped goes off.
   *
   * Runs at `tickHz` rather than every frame because it is an O(mines x enemies)
   * scan whose answer cannot change meaningfully in 16 ms — a soldier crosses about
   * 7 cm in that time, against a trip radius of three metres.
   */
  _proximityScan() {
    if (!this.items.length) return;
    const enemies = this.ctx.ai?.enemies;

    for (let i = this.items.length - 1; i >= 0; i--) {
      const it = this.items[i];
      const def = it.def;
      if (def.trigger !== TRIGGER.PROXIMITY || !it.armed) continue;

      if (it.tripped) {
        it.tripT -= 1 / TUNING.tickHz;
        if (it.tripT <= 0) {
          this._detonate(def, it.pos.clone(), null);
          this._remove(i);
        }
        continue;
      }

      const trip = def.trip ?? 3;
      let found = false;
      if (enemies) {
        for (const e of enemies) {
          if (e.dead) continue;
          if (e.position.distanceToSquared(it.pos) <= trip * trip) {
            found = true;
            break;
          }
        }
      }
      if (found) {
        it.tripped = true;
        it.tripT = def.triggerDelay ?? 0.3;
        this.ctx.events.emit('audio:play', {
          id: 'dryfire',
          position: it.pos.clone(),
          volume: 0.5,
          pitch: 1.6,
        });
      }
    }
  }

  /* ------------------------------- detonation ------------------------------ */

  _detonate(def, at, hit) {
    this.detonations++;
    switch (def.effect) {
      case EFFECT.BLAST:
        explode(this.ctx, at, {
          power: Math.min(1.4, def.radius / 6),
          radius: def.radius,
          damage: def.damage,
          source: this.player,
        });
        break;
      case EFFECT.BURN:
        this._spawnPool(def, at);
        break;
      case EFFECT.CONCUSS:
        this._areaStatus(def, at, 'slow', def.duration, def.strength ?? 0.4, false);
        break;
      case EFFECT.FLASH:
        this._areaStatus(def, at, 'blind', def.duration, 1, true);
        break;
      case EFFECT.SMOKE:
        this._deploySmoke(def, at);
        break;
      case EFFECT.SENSOR:
        this._deploySensor(def, at);
        break;
      case EFFECT.SHOCK:
        this._areaStatus(def, at, 'inhibit', def.duration, 1, false);
        this._areaStatus(def, at, 'slow', def.duration, def.strength ?? 0.2, false);
        break;
      default:
        break;
    }
    this.ctx.events.emit('equipment:detonate', { id: def.id, effect: def.effect, at: at.clone() });
    void hit;
  }

  /**
   * Apply a status to every enemy in radius.
   *
   * `needsSight` is what separates a flash from a concussion and it is not a detail:
   * a flash that works through a wall is a flash nobody can play around, and playing
   * around it — breaking line of sight, turning away — is the entire counter.
   */
  _areaStatus(def, at, kind, seconds, strength, needsSight) {
    const enemies = this.ctx.ai?.enemies;
    if (!enemies) return;
    const physics = this.ctx.physics;
    const r2 = def.radius * def.radius;
    let affected = 0;

    for (const e of enemies) {
      if (e.dead) continue;
      _p.copy(e.position);
      _p.y += 1.0;
      const d2 = _p.distanceToSquared(at);
      if (d2 > r2) continue;
      if (needsSight && physics?.visible && !physics.visible(at, _p, e)) continue;
      // Falls off with distance so the edge of the radius is a graze, not a full
      // effect that stops dead at an invisible line.
      const t = 1 - Math.sqrt(d2) / def.radius;
      const secs = seconds * (0.35 + 0.65 * t);
      e.applyStatus?.(kind, secs, kind === 'slow' ? 1 - (1 - strength) * t : strength);
      affected++;
    }

    this.ctx.events.emit('equipment:affected', { id: def.id, kind, count: affected });
    // The thrower gets the same treatment from their own flash. Anything else makes
    // the flash a free action, and a free action is not a tactical decision.
    if (needsSight && this.player) {
      _p.copy(this.player.position);
      _p.y += 1.5;
      const d = _p.distanceTo(at);
      if (d <= def.radius && (!physics?.visible || physics.visible(at, _p, null))) {
        const t = 1 - d / def.radius;
        this.ctx.events.emit('player:flash', { seconds: seconds * (0.3 + 0.7 * t) });
      }
    }
  }

  _spawnPool(def, at, owner = null) {
    this.pools.push({
      def,
      pos: at.clone(),
      left: def.duration ?? 6,
      acc: 0,
      /** Whose fire this is. Only the owner pays the reduced rate. */
      owner: owner ?? this.player,
    });
    this.ctx.events.emit('fx:explosion', { position: at.clone(), power: 0.35 });
    this.ctx.events.emit('audio:play', { id: 'explosion', position: at.clone(), volume: 0.5 });
  }

  /**
   * Burn pools tick damage rather than dealing it once.
   *
   * That is the whole point of area denial: the cost of the ground is paid per
   * second of standing on it, so it is escapable and therefore a *choice*, not a
   * delayed grenade.
   */
  _stepPools(dt) {
    if (!this.pools.length) return;
    const match = this.ctx.match;

    for (let i = this.pools.length - 1; i >= 0; i--) {
      const pool = this.pools[i];
      pool.left -= dt;
      pool.acc += dt;

      if (pool.acc >= 1 / TUNING.tickHz) {
        const share = pool.acc;
        pool.acc = 0;
        const r2 = pool.def.radius * pool.def.radius;
        const dps = pool.def.damage ?? 20;

        /**
         * One sweep over everyone alive, rather than the enemy list plus a separate
         * hand-written case for the player. Fire does not check whose it is —
         * standing in your own is a mistake the game should let you make — so the
         * owner is passed as the source and lands in the `src === dst` self-damage
         * path deliberately, at the softer rate it has always used.
         */
        for (const reg of match?.living() ?? []) {
          const a = reg.actor;
          // Streaks are registered as combatants but have no body to stand in fire.
          if (!a.position || typeof a.health !== 'number') continue;
          if (a.position.distanceToSquared(pool.pos) > r2) continue;
          const self = a === pool.owner;
          match.requestDamage({
            target: a,
            source: pool.owner ?? null,
            amount: dps * share * (self ? TUNING.selfBurnScale : 1),
            hitbox: null,
            point: a.position.clone(),
            normal: _up.clone(),
            dir: _up.clone(),
            means: MEANS.BURN,
            weapon: pool.def.id,
          });
        }
      }

      if (pool.left <= 0) this.pools.splice(i, 1);
      else if (pool.acc === 0) {
        this.ctx.events.emit('fx:fire', {
          position: pool.pos.clone(),
          radius: pool.def.radius,
          left: pool.left,
        });
      }
    }
  }

  _deploySmoke(def, at) {
    // The smoke *field* already exists and already blocks the AI's line of sight —
    // it was built for the underbarrel launcher. Reusing it is the difference
    // between a smoke that works and a smoke that is a particle effect.
    const smoke = this.ctx.behaviours?.smoke;
    if (smoke?.deploy) smoke.deploy(at.x, at.y, at.z);
    else this.ctx.events.emit('fx:smoke', { position: at.clone(), radius: def.radius });
  }

  _deploySensor(def, at) {
    const intel = this.ctx.intel;
    if (!intel) return;
    intel.ids = intel.ids ?? new Set();
    const enemies = this.ctx.ai?.enemies ?? [];
    const r2 = def.radius * def.radius;
    const marked = [];
    for (const e of enemies) {
      if (e.dead) continue;
      if (e.position.distanceToSquared(at) > r2) continue;
      intel.ids.add(e.id);
      marked.push(e.id);
    }
    this.ctx.events.emit('equipment:sensor', { at: at.clone(), radius: def.radius, marked });
    // Marks expire; they are a snapshot of who was there, not permanent vision.
    setTimeout(() => {
      for (const id of marked) intel.ids?.delete(id);
    }, (def.duration ?? 12) * 1000);
  }

  _applyStim(def) {
    const p = this.player;
    if (!p) return;
    const max = 100;
    const before = p.health ?? 0;
    p.health = Math.min(max, before + (def.heal ?? 60));
    this.ctx.events.emit('player:health', { health: p.health, max });

    // Most of a stim's value is skipping the regeneration delay, not the instant
    // health — it is what lets you re-enter a fight you were losing. The player's
    // regen gate is `elapsed - lastDamageTime < regenDelay`, so pushing the last
    // damage far into the past opens it. Reaching in like this is not lovely, but
    // the alternative is a `heal()` on Player that only this file would ever call.
    if (typeof p.lastDamageTime === 'number') p.lastDamageTime = -999;
    this.ctx.events.emit('equipment:stim', { healed: (p.health ?? 0) - before });
    this.ctx.events.emit('audio:play', { id: 'ui_open', volume: 0.5, pitch: 0.8 });
  }

  /* --------------------------------- misc --------------------------------- */

  /** The roster, so other modules can read it without importing the table. Mirrors
   *  `WeaponSystem.defs`, which exists for the same reason. */
  get defs() {
    return EQUIPMENT;
  }

  report() {
    return {
      loadout: { ...this.loadout },
      count: { ...this.count },
      live: this.items.length,
      pools: this.pools.length,
      remotes: this.remotes.length,
      thrown: this.thrown,
      detonations: this.detonations,
      cooking: this.pending?.slot ?? null,
    };
  }

  dispose() {
    for (const off of this._offs) off();
    this._offs.length = 0;
    while (this.items.length) this._remove(0);
    this.pools.length = 0;
  }
}
